import { useEffect, useRef, useState } from "react";
import { Search, Loader2, GitMerge, X, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Onglet Clients (demande du propriétaire, 2026-10-16) :
// 1. RECHERCHE de compte par nom, prénom, courriel, adresse ou téléphone
//    (serveur — couvre TOUS les comptes) ; un clic ouvre la fiche client.
// 2. FUSION EN UN CLIC de deux comptes qui sont la même personne (admin) :
//    le compte « absorbé » donne tout au compte « conservé » puis est archivé.

function ChampRecherche({ testid, placeholder, onPick, exclus, valeur }) {
  const [terme, setTerme] = useState("");
  const [resultats, setResultats] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [choisi, setChoisi] = useState(null);
  const timer = useRef(null);

  // Préremplissage piloté par le parent (suggestion de doublon → « Préparer »)
  useEffect(() => {
    if (valeur) setChoisi(valeur);
  }, [valeur]);

  useEffect(() => {
    const t = terme.trim();
    if (t.length < 2) { setResultats(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get("/vendeur/clients", { params: { q: t } })
        .then(({ data }) => setResultats((data.clients || []).filter((c) => c.email !== exclus)))
        .catch(() => setResultats([]));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [terme, exclus]);

  return (
    <div className="relative" onBlur={(e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setOuvert(false);
    }}>
      {choisi ? (
        <div className="flex items-center gap-2 bg-ink border border-gold/30 rounded-lg px-4 py-2.5 text-sm"
          data-testid={`${testid}-choisi`}>
          <span className="text-ivory truncate">{choisi.name || "(sans nom)"} — {choisi.email}</span>
          <button type="button" onClick={() => { setChoisi(null); setTerme(""); onPick(null); }}
            className="ml-auto text-ivory/50 hover:text-red-400 transition-colors" data-testid={`${testid}-effacer`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <input value={terme} data-testid={testid}
          onChange={(e) => { setTerme(e.target.value); setOuvert(true); }}
          onFocus={() => setOuvert(true)}
          placeholder={placeholder}
          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
      )}
      {ouvert && !choisi && resultats && resultats.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-ink border border-ivory/15 rounded-lg max-h-56 overflow-y-auto shadow-xl"
          data-testid={`${testid}-resultats`}>
          {resultats.map((c) => (
            <button type="button" key={c.email} data-testid={`${testid}-choix-${c.email}`}
              onClick={() => { setChoisi(c); setOuvert(false); onPick(c); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface transition-colors duration-200 border-b border-ivory/5 last:border-0">
              <span className="text-ivory">{c.name || "(sans nom)"}</span>
              <span className="text-ivory/50"> — {c.email}</span>
              {c.phone && <span className="text-ivory/40"> · {c.phone}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RechercheFusionClients({ isAdmin, onOuvrirFiche, onFusionFaite }) {
  const [source, setSource] = useState(null);   // doublon absorbé
  const [cible, setCible] = useState(null);     // compte conservé
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [purge, setPurge] = useState(null);   // {apercu: n} en attente de confirmation
  const [purgeBusy, setPurgeBusy] = useState(false);

  // « Pas un doublon » : la paire écartée ne revient jamais (2026-10-16)
  const ignorer = async (s, i) => {
    try {
      await api.post("/admin/clients/doublons/ignorer",
        { emails: s.comptes.map((c) => c.email) });
      setSuggestions((ls) => ls.filter((_, j) => j !== i));
      toast.success("Suggestion écartée — elle ne reviendra plus.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Nettoyage des comptes de TEST (demande du propriétaire, 2026-10-16) :
  // 1er clic = aperçu du nombre, 2e clic = suppression réelle
  const purger = async () => {
    setPurgeBusy(true);
    try {
      if (!purge) {
        const { data } = await api.post("/admin/clients/purge-tests", { apercu: true });
        if (!data.comptes) { toast.success("Aucun compte de test à nettoyer."); return; }
        setPurge(data.comptes);
        return;
      }
      const { data } = await api.post("/admin/clients/purge-tests", {});
      toast.success(`${data.comptes} comptes de test supprimés (sessions, paniers et jetons liés inclus).`);
      setPurge(null);
      chargerSuggestions();
      onFusionFaite?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPurgeBusy(false);
    }
  };

  const chargerSuggestions = () => {
    if (!isAdmin) return;
    api.get("/admin/clients/doublons")
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => setSuggestions([]));
  };
  useEffect(chargerSuggestions, [isAdmin]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fusionner = async () => {
    if (!source || !cible) return;
    if (!window.confirm(
      `Fusionner les deux comptes ?\n\nABSORBÉ (archivé) : ${source.email}\nCONSERVÉ : ${cible.email}\n\n`
      + "Soumissions, commandes et adresses du compte absorbé passeront au compte conservé. "
      + "Cette action est définitive.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clients/fusion",
        { source_email: source.email, cible_email: cible.email });
      toast.success(`Comptes fusionnés : ${data.soumissions} soumission(s), ${data.commandes} commande(s) `
        + `et ${data.adresses_ajoutees} adresse(s) passées à ${data.conserve}.`);
      setSource(null);
      chargerSuggestions();
      onFusionFaite?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-5 mb-6 space-y-4" data-testid="recherche-clients-admin">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-gold mb-2 flex items-center gap-2">
          <Search className="w-3.5 h-3.5" /> Repérer un compte (nom, prénom, courriel, association, adresse ou téléphone)
        </p>
        <ChampRecherche testid="admin-client-search"
          placeholder="Chercher parmi tous les comptes…"
          onPick={(c) => c && onOuvrirFiche?.(c)} />
      </div>

      {isAdmin && (
        <div className="border-t border-ivory/10 pt-4" data-testid="fusion-clients-card">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-2 flex items-center gap-2">
            <GitMerge className="w-3.5 h-3.5" /> Fusionner deux comptes (même personne)
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-red-300/80 mb-1">Compte absorbé (doublon, sera archivé)</p>
              <ChampRecherche testid="fusion-source" placeholder="Chercher le doublon…"
                onPick={setSource} exclus={cible?.email} valeur={source} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-1">Compte conservé (reçoit tout)</p>
              <ChampRecherche testid="fusion-cible" placeholder="Chercher le compte à garder…"
                onPick={setCible} exclus={source?.email} valeur={cible} />
            </div>
          </div>
          <button type="button" data-testid="fusion-lancer" disabled={!source || !cible || busy}
            onClick={fusionner}
            className="mt-3 inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2 rounded-full text-xs hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />}
            Fusionner les deux comptes
          </button>

          {/* Suggestions automatiques de doublons (demande du propriétaire,
              2026-10-16) : même téléphone, adresse ou nom → préparer la fusion */}
          <div className="mt-4" data-testid="doublons-suggestions">
            <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-2">Doublons probables détectés</p>
            {suggestions === null && <p className="text-xs text-ivory/50">Analyse des comptes…</p>}
            {suggestions?.length === 0 && (
              <p className="text-xs text-ivory/50">Aucun doublon probable — les carnets sont propres.</p>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {suggestions?.map((s, i) => (
                <div key={i} data-testid={`doublon-suggestion-${i}`}
                  className="flex flex-wrap items-center gap-2 bg-ink border border-amber-400/25 rounded-lg px-3 py-2.5">
                  <span className="text-[10px] uppercase tracking-widest text-amber-300/90 shrink-0">{s.raison}</span>
                  <span className="text-xs text-ivory/80 flex-1 min-w-0 truncate">
                    {s.comptes.map((c) => c.email).join("  ↔  ")}
                  </span>
                  <button type="button" data-testid={`doublon-preparer-${i}`}
                    onClick={() => { setCible(s.comptes[0]); setSource(s.comptes[s.comptes.length - 1]); }}
                    className="shrink-0 inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1 rounded-full text-[11px] hover:bg-gold hover:text-ink transition-colors duration-300">
                    <GitMerge className="w-3 h-3" /> Préparer la fusion
                  </button>
                  <button type="button" data-testid={`doublon-ignorer-${i}`} onClick={() => ignorer(s, i)}
                    title="Écarter pour toujours — ces comptes ne sont pas la même personne"
                    className="shrink-0 inline-flex items-center gap-1.5 border border-ivory/20 text-ivory/60 px-3 py-1 rounded-full text-[11px] hover:border-ivory/50 transition-colors duration-300">
                    <EyeOff className="w-3 h-3" /> Pas un doublon
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Nettoyage des comptes de TEST avant la revue des fusions
              (demande du propriétaire, 2026-10-16) — deux clics : aperçu, puis
              suppression confirmée */}
          <div className="mt-4 border-t border-ivory/10 pt-3 flex items-center gap-3 flex-wrap">
            <button type="button" data-testid="purge-tests-btn" disabled={purgeBusy} onClick={purger}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] border transition-colors duration-300 disabled:opacity-50 ${
                purge ? "border-red-500/60 text-red-300 hover:bg-red-500/10 font-semibold"
                      : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              {purgeBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              {purge ? `Confirmer : supprimer ${purge} comptes de test ?`
                     : "Nettoyer les comptes de test (TEST QA…)"}
            </button>
            {purge !== null && (
              <button type="button" data-testid="purge-tests-annuler" onClick={() => setPurge(null)}
                className="text-[11px] text-ivory/50 hover:text-ivory transition-colors">
                Annuler
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
